<template>
  <teleport :to="teleportTarget">
    <div>
      <!-- Alert Modal -->
      <Transition name="modal">
        <div v-if="state.alert.show" class="modal-overlay" @click="handleAlertConfirm">
          <div class="modal-dialog" @click.stop>
            <div class="modal-content" :class="`modal-${state.alert.type}`">
              <div class="modal-icon">
                <i v-if="state.alert.type === 'success'" class="bi bi-check-circle-fill"></i>
                <i v-else-if="state.alert.type === 'error'" class="bi bi-x-circle-fill"></i>
                <i v-else-if="state.alert.type === 'warning'" class="bi bi-exclamation-triangle-fill"></i>
                <i v-else class="bi bi-info-circle-fill"></i>
              </div>
              
              <h3 class="modal-title">{{ state.alert.title }}</h3>
              <p class="modal-message">{{ state.alert.message }}</p>
              
              <div class="modal-actions">
                <button 
                  class="btn btn-primary" 
                  @click="handleAlertConfirm"
                >
                  {{ state.alert.confirmText }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Transition>

      <!-- Confirm Modal -->
      <Transition name="modal">
        <div v-if="state.confirm.show" class="modal-overlay" @click="handleConfirmCancel">
          <div class="modal-dialog" @click.stop>
            <div class="modal-content" :class="`modal-${state.confirm.type}`">
              <div class="modal-icon">
                <i v-if="state.confirm.type === 'error'" class="bi bi-trash-fill"></i>
                <i v-else-if="state.confirm.type === 'warning'" class="bi bi-exclamation-triangle-fill"></i>
                <i v-else class="bi bi-question-circle-fill"></i>
              </div>
              
              <h3 class="modal-title">{{ state.confirm.title }}</h3>
              <p class="modal-message">{{ state.confirm.message }}</p>
              
              <div v-if="state.confirm.showToggle" class="form-check form-switch d-flex align-items-center justify-content-center mb-4 gap-2">
                 <input class="form-check-input mt-0" type="checkbox" :id="'confirmToggle'" v-model="state.confirm.toggleValue">
                 <label class="form-check-label text-start" style="font-size: 0.9rem;" :for="'confirmToggle'">{{ state.confirm.toggleLabel }}</label>
              </div>

              <div class="modal-actions">
                <button 
                  class="btn btn-secondary" 
                  @click="handleConfirmCancel"
                >
                  {{ state.confirm.cancelText }}
                </button>
                <button 
                  class="btn btn-primary" 
                  :class="state.confirm.type === 'error' ? 'btn-danger' : ''"
                  @click="handleConfirmConfirm"
                >
                  {{ state.confirm.confirmText }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Transition>

      <!-- Prompt Modal -->
      <Transition name="modal">
        <div v-if="state.prompt.show" class="modal-overlay" @click="handlePromptCancel">
          <div class="modal-dialog" @click.stop>
            <div class="modal-content modal-info">
              <div class="modal-icon">
                <i class="bi bi-pencil-square"></i>
              </div>
              
              <h3 class="modal-title">{{ state.prompt.title }}</h3>
              <p class="modal-message">{{ state.prompt.message }}</p>
              
              <div class="mb-4">
                <input 
                  type="text" 
                  class="form-control" 
                  v-model="state.prompt.value"
                  :placeholder="state.prompt.placeholder"
                  @keyup.enter="handlePromptConfirm"
                  ref="promptInput"
                >
              </div>
              
              <div class="modal-actions">
                <button 
                  class="btn btn-secondary" 
                  @click="handlePromptCancel"
                >
                  {{ state.prompt.cancelText }}
                </button>
                <button 
                  class="btn btn-primary" 
                  @click="handlePromptConfirm"
                >
                  {{ state.prompt.confirmText }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </div>
  </teleport>
</template>

<script>
import { useDialog } from '../composables/useDialog'
import { ref, computed, onMounted, onUnmounted } from 'vue'

export default {
  name: 'DialogModals',
  setup() {
    const { state } = useDialog()
    const isFullscreen = ref(false)

    const teleportTarget = computed(() => {
      if (isFullscreen.value && document.fullscreenElement) {
        return document.fullscreenElement
      }
      return 'body'
    })

    const onFullscreenChange = () => {
      isFullscreen.value = !!document.fullscreenElement
    }

    onMounted(() => {
      document.addEventListener('fullscreenchange', onFullscreenChange)
      document.addEventListener('webkitfullscreenchange', onFullscreenChange)
      document.addEventListener('mozfullscreenchange', onFullscreenChange)
      document.addEventListener('MSFullscreenChange', onFullscreenChange)
    })

    onUnmounted(() => {
      document.removeEventListener('fullscreenchange', onFullscreenChange)
      document.removeEventListener('webkitfullscreenchange', onFullscreenChange)
      document.removeEventListener('mozfullscreenchange', onFullscreenChange)
      document.removeEventListener('MSFullscreenChange', onFullscreenChange)
    })

    const handleAlertConfirm = () => {
      if (state.alert.onConfirm) {
        state.alert.onConfirm()
      }
    }

    const handleConfirmConfirm = () => {
      if (state.confirm.onConfirm) {
        state.confirm.onConfirm()
      }
    }

    const handleConfirmCancel = () => {
      if (state.confirm.onCancel) {
        state.confirm.onCancel()
      }
    }

    const handlePromptConfirm = () => {
      if (state.prompt.onConfirm) {
        state.prompt.onConfirm(state.prompt.value)
      }
    }

    const handlePromptCancel = () => {
      if (state.prompt.onCancel) {
        state.prompt.onCancel()
      }
    }

    return {
      state,
      teleportTarget,
      handleAlertConfirm,
      handleConfirmConfirm,
      handleConfirmCancel,
      handlePromptConfirm,
      handlePromptCancel
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2147483647 !important;
  padding: 1rem;
}

.modal-dialog {
  width: 100%;
  max-width: 450px;
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.modal-content {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  text-align: center;
  position: relative;
  overflow: hidden;
}

.modal-content::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #667eea, #764ba2);
}

.modal-content.modal-success::before {
  background: linear-gradient(90deg, #10b981, #059669);
}

.modal-content.modal-error::before {
  background: linear-gradient(90deg, #ef4444, #dc2626);
}

.modal-content.modal-warning::before {
  background: linear-gradient(90deg, #f59e0b, #d97706);
}

.modal-icon {
  font-size: 3.5rem;
  margin-bottom: 1rem;
  animation: iconPop 0.5s ease-out;
}

@keyframes iconPop {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.modal-info .modal-icon {
  color: #3b82f6;
}

.modal-success .modal-icon {
  color: #10b981;
}

.modal-error .modal-icon {
  color: #ef4444;
}

.modal-warning .modal-icon {
  color: #f59e0b;
}

.modal-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 0.75rem;
}

.modal-message {
  font-size: 1rem;
  color: #6b7280;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  white-space: pre-line;
}

.modal-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: center;
}

.btn {
  padding: 0.75rem 2rem;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 120px;
}

.btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.btn:active {
  transform: translateY(0);
}

.btn-primary {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
}

.btn-primary:hover {
  background: linear-gradient(135deg, #5a6fd8, #6a4190);
}

.btn-danger {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
}

.btn-danger:hover {
  background: linear-gradient(135deg, #dc2626, #b91c1c);
}

.btn-secondary {
  background: #f3f4f6;
  color: #4b5563;
}

.btn-secondary:hover {
  background: #e5e7eb;
}

/* Transitions */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .modal-dialog,
.modal-leave-active .modal-dialog {
  transition: transform 0.3s ease;
}

.modal-enter-from .modal-dialog,
.modal-leave-to .modal-dialog {
  transform: translateY(20px) scale(0.95);
}

/* Responsive */
@media (max-width: 576px) {
  .modal-dialog {
    max-width: 100%;
  }

  .modal-content {
    padding: 1.5rem;
  }

  .modal-icon {
    font-size: 2.5rem;
  }

  .modal-title {
    font-size: 1.25rem;
  }

  .modal-actions {
    flex-direction: column-reverse;
  }

  .btn {
    width: 100%;
  }
}
</style>
